import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { TodoTag } from '@/types/todo';

interface TagManagerDialogProps {
  open: boolean;
  availableTags: TodoTag[];
  selectedTagIds: string[];
  onClose: () => void;
  onSubmit: (tagIds: string[]) => void;
  onCreateTag: (payload: { name: string; color?: string | null }) => Promise<TodoTag>;
  onUpdateTag: (tagId: string, payload: { name?: string; color?: string | null }) => Promise<TodoTag>;
  onDeleteTag: (tagId: string) => Promise<void>;
}

export function TagManagerDialog({
  open,
  availableTags,
  selectedTagIds,
  onClose,
  onSubmit,
  onCreateTag,
  onUpdateTag,
  onDeleteTag,
}: TagManagerDialogProps) {
  const [selection, setSelection] = useState(new Set(selectedTagIds));
  const [mode, setMode] = useState<'create' | 'edit'>('create');
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [color, setColor] = useState('#f97316');

  useEffect(() => {
    if (open) {
      setSelection(new Set(selectedTagIds));
    }
  }, [open, selectedTagIds]);

  const sortedTags = useMemo(
    () => [...availableTags].sort((a, b) => a.name.localeCompare(b.name)),
    [availableTags]
  );

  if (!open) {
    return null;
  }

  const toggleTag = (tagId: string) => {
    const next = new Set(selection);
    if (next.has(tagId)) {
      next.delete(tagId);
    } else {
      next.add(tagId);
    }
    setSelection(next);
  };

  const resetForm = () => {
    setMode('create');
    setEditingTagId(null);
    setName('');
    setColor('#f97316');
  };

  const startEdit = (tag: TodoTag) => {
    setMode('edit');
    setEditingTagId(tag.id);
    setName(tag.name);
    setColor(tag.color ?? '#f97316');
  };

  const handleSaveTag = async () => {
    if (!name.trim()) {
      return;
    }
    if (mode === 'create') {
      const newTag = await onCreateTag({ name: name.trim(), color });
      setSelection((prev) => new Set(prev).add(newTag.id));
    } else if (editingTagId) {
      await onUpdateTag(editingTagId, { name: name.trim(), color });
    }
    resetForm();
  };

  const handleDeleteTag = async (tagId: string) => {
    await onDeleteTag(tagId);
    setSelection((prev) => {
      const next = new Set(prev);
      next.delete(tagId);
      return next;
    });
    if (editingTagId === tagId) {
      resetForm();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg space-y-4 max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Manage Tags</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 overflow-y-auto flex-1 min-h-0">
          {sortedTags.length === 0 ? (
            <p className="text-sm text-muted-foreground">No tags yet.</p>
          ) : (
            sortedTags.map((tag) => (
              <div key={tag.id} className="flex items-center justify-between rounded-2xl border border-border/60 px-3 py-2">
                <div className="flex items-center gap-3">
                  <Checkbox checked={selection.has(tag.id)} onCheckedChange={() => toggleTag(tag.id)} />
                  <span className="flex items-center gap-2 text-sm">
                    <span
                      className="h-4 w-4 rounded-full border border-border/40"
                      style={{ backgroundColor: tag.color ?? '#94a3b8' }}
                    />
                    {tag.name}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={() => startEdit(tag)}>
                    Edit
                  </Button>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDeleteTag(tag.id)}>
                    Delete
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="space-y-3 rounded-2xl border border-border/60 p-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold">{mode === 'create' ? 'Create tag' : 'Edit tag'}</p>
            {mode === 'edit' && (
              <Button size="sm" variant="ghost" onClick={resetForm}>
                New tag
              </Button>
            )}
          </div>
          <div>
            <Label>Name</Label>
            <Input value={name} onChange={(event) => setName(event.target.value)} placeholder="Follow up" />
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <Label>Color</Label>
              <Input type="color" value={color} onChange={(event) => setColor(event.target.value)} className="h-10" />
            </div>
            <div className="flex flex-1 flex-col gap-1">
              <Label>&nbsp;</Label>
              <Button variant="secondary" onClick={handleSaveTag} disabled={!name.trim()}>
                {mode === 'create' ? 'Add Tag' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2 flex-shrink-0">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => onSubmit(Array.from(selection))}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
